import { getTodayAsISODate } from '@/utils/date-operations';
import React, { useState, useMemo } from 'react';
import { useModalKeyboardPrevention } from '../../hooks/useKeyboardShortcuts';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Alert } from '../ui/alert';
import { Label } from '../ui/label';
import { AlertCircle, Trash2, Plus } from 'lucide-react';
import { useCreateAdjustmentEntry } from '../../hooks/react-query/useGeneralLedger';
import { useChartOfAccountsGrouped } from '../../hooks/react-query/useChartOfAccounts';
import type { Fund } from '../../types';

interface JournalLine {
  account_code: string;
  debit_amount: number;
  credit_amount: number;
}

type JournalLineRow = JournalLine & { _key: string };

interface CreateAdjustmentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  fund: Fund;
}

export function CreateAdjustmentModal({
  open,
  onClose,
  onSuccess,
  fund,
}: CreateAdjustmentModalProps) {
  // Fetch from global chart of accounts lookup - only when modal is open
  const { data: chartOfAccountsGrouped, isLoading: isLoadingAccounts } =
    useChartOfAccountsGrouped({ enabled: open });

  // Flatten grouped accounts into a sorted list for the dropdown
  const accountsList = useMemo(() => {
    if (!chartOfAccountsGrouped) return [];
    const all = [
      ...(chartOfAccountsGrouped.assets || []),
      ...(chartOfAccountsGrouped.liabilities || []),
      ...(chartOfAccountsGrouped.equity || []),
      ...(chartOfAccountsGrouped.income || []),
      ...(chartOfAccountsGrouped.expenses || []),
    ];
    return all.sort((a, b) => a.account_code.localeCompare(b.account_code));
  }, [chartOfAccountsGrouped]);

  const [transactionDate, setTransactionDate] = useState(() =>
    getTodayAsISODate()
  );
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [journalLines, setJournalLines] = useState<JournalLineRow[]>([
    {
      _key: crypto.randomUUID(),
      account_code: '',
      debit_amount: 0,
      credit_amount: 0,
    },
    {
      _key: crypto.randomUUID(),
      account_code: '',
      debit_amount: 0,
      credit_amount: 0,
    },
  ]);

  const createAdjustmentMutation = useCreateAdjustmentEntry();

  // Prevent page-level keyboard shortcuts (like 1-8 for tab switching) while modal is open
  useModalKeyboardPrevention(open, 'create-adjustment-modal');

  const error = createAdjustmentMutation.error as
    (Error & { detail?: string }) | null;
  const errorMessage = error?.detail || error?.message || null;

  const handleSubmit = (e: React.SubmitEvent) => {
    e.preventDefault();

    createAdjustmentMutation.mutate(
      {
        fundId: fund.id,
        description,
        adjustments: journalLines
          .filter(
            (line) =>
              line.account_code &&
              (line.debit_amount > 0 || line.credit_amount > 0)
          )
          .map(({ _key: _, ...line }) => line),
        options: {
          reference: reference || undefined,
          document_date: transactionDate,
        },
      },
      { onSuccess }
    );
  };

  const addLine = () => {
    setJournalLines([
      ...journalLines,
      {
        _key: crypto.randomUUID(),
        account_code: '',
        debit_amount: 0,
        credit_amount: 0,
      },
    ]);
  };

  const removeLine = (index: number) => {
    if (journalLines.length > 1) {
      setJournalLines(journalLines.filter((_, i) => i !== index));
    }
  };

  const updateLine = (
    index: number,
    field: keyof JournalLine,
    value: string | number
  ) => {
    const newLines = [...journalLines];
    newLines[index] = { ...newLines[index], [field]: value } as JournalLineRow;
    setJournalLines(newLines);
  };

  const totalDebits = journalLines.reduce(
    (sum, line) => sum + (line.debit_amount || 0),
    0
  );
  const totalCredits = journalLines.reduce(
    (sum, line) => sum + (line.credit_amount || 0),
    0
  );
  const isBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader>
          <DialogTitle>Create Adjustment Entry</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {errorMessage && (
            <Alert variant="destructive" className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-medium">Error creating adjustment entry</p>
                <p className="text-sm">{errorMessage}</p>
              </div>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={transactionDate}
                onChange={(e) => setTransactionDate(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Reference</Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>

          <div>
            <Label>Description</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Entry description"
              className="mt-2"
            />
          </div>

          <div>
            <Label>Journal Lines</Label>
            <div className="space-y-3 mt-2">
              {journalLines.map((line, index) => (
                <div key={line._key} className="flex items-center gap-2">
                  <Select
                    value={line.account_code}
                    onValueChange={(value) =>
                      updateLine(index, 'account_code', value)
                    }
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Select Account" />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingAccounts ? (
                        <SelectItem value="" disabled>
                          Loading accounts...
                        </SelectItem>
                      ) : (
                        accountsList.map((account) => (
                          <SelectItem
                            key={account.account_code}
                            value={account.account_code}
                          >
                            {account.account_code} - {account.account_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Debit"
                    value={line.debit_amount || ''}
                    onChange={(e) =>
                      updateLine(
                        index,
                        'debit_amount',
                        Number.parseFloat(e.target.value) || 0
                      )
                    }
                    className="w-28"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Credit"
                    value={line.credit_amount || ''}
                    onChange={(e) =>
                      updateLine(
                        index,
                        'credit_amount',
                        Number.parseFloat(e.target.value) || 0
                      )
                    }
                    className="w-28"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLine(index)}
                    disabled={journalLines.length <= 1}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addLine}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm mt-3"
            >
              <Plus className="h-4 w-4" />
              Add Line
            </button>
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              Total Debit: <span className="font-medium">{totalDebits}</span>
              <span className="mx-4">
                Total Credit:{' '}
                <span className="font-medium">{totalCredits}</span>
              </span>
              <span
                className={
                  isBalanced
                    ? 'text-green-600 font-medium'
                    : 'text-red-600 font-medium'
                }
              >
                {isBalanced ? 'Balanced' : 'Unbalanced'}
              </span>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  createAdjustmentMutation.isPending ||
                  !isBalanced ||
                  journalLines.every((line) => !line.account_code)
                }
              >
                {createAdjustmentMutation.isPending
                  ? 'Creating...'
                  : 'Create Entry'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
